import { useState } from 'react';
import { Download, GripVertical, X } from 'lucide-react';
import { PDFDocument } from 'pdf-lib';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Dropzone } from '@/components/ui/Dropzone';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { formatBytes, readFileAsArrayBuffer, bytesToBlob } from '@/lib/utils';

export function PdfMerge() {
  const [files, setFiles] = useState<File[]>([]);
  const [working, setWorking] = useState(false);

  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= files.length) return;
    setFiles((arr) => {
      const out = [...arr];
      [out[i], out[j]] = [out[j], out[i]];
      return out;
    });
  }

  async function merge() {
    if (files.length < 2) {
      toast.error('Add at least two PDFs');
      return;
    }
    setWorking(true);
    try {
      const out = await PDFDocument.create();
      for (const f of files) {
        const buf = await readFileAsArrayBuffer(f);
        const src = await PDFDocument.load(buf);
        const pages = await out.copyPages(src, src.getPageIndices());
        pages.forEach((p) => out.addPage(p));
      }
      const bytes = await out.save();
      saveAs(bytesToBlob(bytes), 'merged.pdf');
      toast.success('PDFs merged');
    } catch (e) {
      toast.error('Failed to merge PDFs');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="pdf-merge"
        title="Merge PDFs"
        description="Combine multiple PDF files into one. Reorder pages by drag or arrows."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <Dropzone
            accept="application/pdf"
            multiple
            onFiles={(f) => setFiles((p) => [...p, ...f])}
            hint="Drop multiple PDFs — order them below"
            title="Add PDFs"
          />
          {files.length > 0 && (
            <Card className="!p-2">
              {files.map((f, i) => (
                <div
                  key={`${f.name}-${i}`}
                  className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-surface-hover group"
                >
                  <span className="text-fg-subtle">
                    <GripVertical size={12} />
                  </span>
                  <span className="text-2xs text-fg-subtle font-mono w-6 text-right">
                    {i + 1}
                  </span>
                  <span className="flex-1 truncate text-sm">{f.name}</span>
                  <span className="text-2xs text-fg-subtle font-mono">{formatBytes(f.size)}</span>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
                    <button
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      className="size-6 rounded hover:bg-surface text-fg-subtle hover:text-fg disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => move(i, 1)}
                      disabled={i === files.length - 1}
                      className="size-6 rounded hover:bg-surface text-fg-subtle hover:text-fg disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() =>
                        setFiles((arr) => arr.filter((_, idx) => idx !== i))
                      }
                      className="size-6 rounded hover:bg-surface text-fg-subtle hover:text-danger"
                    >
                      <X size={11} />
                    </button>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </div>

        <Card>
          <div className="text-xs font-medium text-fg-muted mb-2">Summary</div>
          <div className="space-y-1.5 text-sm">
            <Row label="Files" value={files.length.toString()} />
            <Row
              label="Total size"
              value={formatBytes(files.reduce((s, f) => s + f.size, 0))}
            />
          </div>
          <Button
            variant="primary"
            className="w-full mt-4"
            disabled={files.length < 2 || working}
            loading={working}
            onClick={merge}
          >
            <Download size={12} />
            Merge {files.length || ''}
          </Button>
          <div className="text-2xs text-fg-subtle mt-2 text-center">
            Files are processed locally — never uploaded.
          </div>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-fg-subtle text-xs">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

export default PdfMerge;
